import { useContext, useEffect, useMemo } from 'react';
import { useCookies } from 'react-cookie';
import { useLocation, useNavigate } from 'react-router-dom';

import Location from '@r6digital/rapidstor-types/react/entities/Location';

import { skipToken } from '@reduxjs/toolkit/query';
import { JourneyVariant } from '@r6digital/rapidstor-types/react/entities/Journey';
import { useSearchReservationsQuery } from '@/core/api/rapidstor';
import { selectReservationToRestore } from '@/core/utils/reservationRestore';

import store from '@/store';
import {
    setContactData,
    setReservationData,
    setUnitData,
} from '@/store/bookingSlice';

import { JourneyContext } from '@/features/rapidstor/providers/JourneyProvider';

export default function ReservationRestore({ location }: { location: Location }) {
    const navigate = useNavigate();
    const routerLocation = useLocation();
    const [cookies, , removeCookie] = useCookies(['contactData', 'persona', 'sessionId']);
    const { unitList, unitListLoading } = useContext(JourneyContext);

    const { sessionId } = cookies;

    const queryParams = useMemo(() => {
        if (!sessionId || !location) return skipToken;
        return {
            locationCode: location.locationCode,
            corpCode: location.corpCode,
            'externalData.SessionID': sessionId,
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId, location?.locationCode, location?.corpCode]);

    const { data: reservations, isSuccess } = useSearchReservationsQuery(queryParams, {
        refetchOnMountOrArgChange: false,
    });

    useEffect(() => {
        // These screens address a reservation by token and load it themselves, so there is
        // nothing to restore. Acting here is actively harmful: a reservation awaiting its fee is
        // non-restorable by design (see selectReservationToRestore), so restore resolves to null
        // and redirects the customer to the root — off the very screen that collects that fee,
        // and off the confirmation that reports the outcome. The move-in wizard is addressed
        // the same way, and a saved quote (enquiry, no fee) in the session is non-restorable too.
        const isTokenAddressedWizardRoute = /^\/(reservation-with-fee|move-in|move-in-confirmed|booking-confirmed)(\/|$)/
            .test(routerLocation.pathname);
        if (isTokenAddressedWizardRoute) {
            return;
        }

        if (isSuccess && reservations.length && !unitListLoading && unitList) {
            const reservation = selectReservationToRestore(reservations);
            if (!reservation) {
                store.dispatch(setReservationData(null));
                store.dispatch(setUnitData(null));
                (['contactData', 'persona'] as const).forEach((cookie) => removeCookie(cookie, { path: '/' }));

                if (routerLocation.pathname !== '/') {
                    navigate('/');
                }

                return;
            }

            const unitType = unitList?.unitTypes.find(
                (ut) => ut._id === reservation.unitType?._id,
            );
            const descriptor = unitList?.descriptors.find(
                (d) => d._id === reservation.unitType.descriptor?._id,
            );

            if (unitType && descriptor) {
                const unitToUse = unitType?.units.find(
                    (u) => u._id === reservation.unitType.unitToUse?._id,
                );
                const dealObject = unitType?.deals.find(
                    (d) => d._id === reservation.deal?._id,
                );
                store.dispatch(
                    setReservationData({
                        unitType,
                        unitToUse,
                        descriptor,
                        deal: reservation.deal?._id,
                        dealObject,
                        contactData: {
                            neededDate: reservation.moveInContext.neededDate ?? undefined,
                            firstName: reservation.firstName,
                            lastName: reservation.lastName,
                            email: reservation.email,
                            phone: reservation.phone,
                        },
                        token: reservation.token,
                    }),
                );

                store.dispatch(
                    setUnitData({
                        location,
                        unitType,
                        descriptors: unitList.descriptors,
                        descriptor,
                    }),
                );
            }

            store.dispatch(
                setContactData({
                    firstName: reservation.firstName,
                    lastName: reservation.lastName,
                    phone: reservation.phone,
                    email: reservation.email,
                    neededDate: reservation.moveInContext.neededDate ?? undefined,
                }),
            );

            if (routerLocation.pathname === '/' && window.rapidstor_settings.app_config.journey.variant === JourneyVariant.JBL) {
                navigate('/customise-booking');
            }
        }
    // We exclude routerLocation from the dependency array to avoid infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSuccess, reservations, location, navigate, unitListLoading, unitList]);

    return null;
}
